import React from 'react';
import {
  Sparkle, ClipboardText, Package, ArrowsClockwise, Truck, CheckCircle, MapPin,
  CurrencyDollar, Receipt, Coins, XCircle, Path,
} from '@phosphor-icons/react';

/**
 * STATUS_META — one entry per ORDER_STATUSES value in server.py / OrdersPage.
 *
 *   { key, label, icon, dotClass, ringClass }
 *
 * dotClass / ringClass drive the colored circle around the icon and the
 * subtle accent ring when the card is the active filter. Keep label SHORT
 * so the row stays scannable.
 */
export const STATUS_META = [
  { key: 'new_order',           label: 'New',              icon: Sparkle,          color: 'bg-sky-100 text-sky-700 ring-sky-300' },
  { key: 'order_submitted',     label: 'Submitted',        icon: ClipboardText,    color: 'bg-blue-100 text-blue-700 ring-blue-300' },
  { key: 'ready_to_load',       label: 'Ready to load',    icon: Package,          color: 'bg-amber-100 text-amber-700 ring-amber-300' },
  { key: 'order_in_progress',   label: 'In progress',      icon: ArrowsClockwise,  color: 'bg-yellow-100 text-yellow-700 ring-yellow-300' },
  { key: 'shipped',             label: 'Shipped',          icon: Truck,            color: 'bg-indigo-100 text-indigo-700 ring-indigo-300' },
  { key: 'arrived',             label: 'Arrived',          icon: MapPin,           color: 'bg-purple-100 text-purple-700 ring-purple-300' },
  { key: 'cleared_customs',     label: 'Cleared customs',  icon: CheckCircle,      color: 'bg-emerald-100 text-emerald-700 ring-emerald-300' },
  { key: 'delivered',           label: 'Delivered',        icon: Path,             color: 'bg-green-100 text-green-700 ring-green-300' },
  { key: 'awaiting_payment',    label: 'Awaiting payment', icon: CurrencyDollar,   color: 'bg-orange-100 text-orange-700 ring-orange-300' },
  { key: 'payment_received',    label: 'Paid',             icon: Coins,            color: 'bg-teal-100 text-teal-700 ring-teal-300' },
  { key: 'invoice_issued',      label: 'Invoiced',         icon: Receipt,          color: 'bg-cyan-100 text-cyan-700 ring-cyan-300' },
  { key: 'cancelled',           label: 'Cancelled',        icon: XCircle,          color: 'bg-rose-100 text-rose-700 ring-rose-300' },
];

const StatusPills = ({ counts = {}, totalAll = 0, active = 'all', onSelect = () => {} }) => {
  return (
    <div className="mb-4" data-testid="status-pills">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-2.5">
        {/* All-orders card */}
        <button
          type="button"
          onClick={() => onSelect('all')}
          className={`flex items-center gap-3 p-3 bg-white border text-left transition-all hover:shadow-sm ${
            active === 'all' ? 'border-primary ring-2 ring-primary/30' : 'border-border'
          }`}
          data-testid="status-pill-all"
        >
          <div className="w-9 h-9 bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
            <ClipboardText weight="duotone" className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">All Orders</div>
            <div className="font-bold text-lg leading-none mt-0.5">{totalAll}</div>
          </div>
        </button>
        {STATUS_META.map(({ key, label, icon: Icon, color }) => {
          const count = counts[key] || 0;
          const isActive = active === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(key)}
              className={`flex items-center gap-3 p-3 bg-white border text-left transition-all hover:shadow-sm ${
                isActive ? 'border-primary ring-2 ring-primary/30' : 'border-border'
              } ${count === 0 ? 'opacity-60' : ''}`}
              data-testid={`status-pill-${key}`}
              title={`Show only "${label}" orders`}
            >
              <div className={`w-9 h-9 flex items-center justify-center shrink-0 ${color.replace(/ring-[^\s]+/, '').trim()}`}>
                <Icon weight="duotone" className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">{label}</div>
                <div className="font-bold text-lg leading-none mt-0.5">{count}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StatusPills;
